import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./Products.module.css";
import ProductCard from "./ProductCard";
import ProductDetailModal from "./Productdetailmodal";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const API_URL = "https://dashboard.rawkemya.com/api/supplier-materials";

export default function Products({ limit = null }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");
  const navigate = useNavigate();
  const isAuthenticated = !!localStorage.getItem("token");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const [selected, setSelected] = useState(null);

  const PAGE_SIZE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const fetchAllProducts = useCallback(async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const headers = {
        Accept: "application/json",
        "Accept-Language": i18n.language,
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const firstRes = await axios.get(API_URL, {
        headers,
        params: { page: 1 },
      });

      if (firstRes.data.code !== 200) {
        setProducts([]);
        return;
      }

      let all = firstRes.data.data || [];
      const lastPage = firstRes.data.pagination?.last_page || 1;

      if (lastPage > 1) {
        const restRes = await Promise.all(
          Array.from({ length: lastPage - 1 }, (_, i) =>
            axios.get(API_URL, { headers, params: { page: i + 2 } }),
          ),
        );

        restRes.forEach((res) => {
          if (res.data.code === 200 && Array.isArray(res.data.data)) {
            all = [...all, ...res.data.data];
          }
        });
      }

      setProducts(all);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, activeCat]);

  const categories = useMemo(() => {
    return ["All", ...new Set(products.map((p) => p.family?.name))].filter(
      Boolean,
    );
  }, [products]);

  const filteredAll = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      const matchCat = activeCat === "All" || p.family?.name === activeCat;
      const matchSearch =
        !q ||
        p.name?.toLowerCase().includes(q) ||
        p.cas_number?.toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [search, activeCat, products]);

  const filtered = useMemo(() => {
    if (limit) return filteredAll.slice(0, limit);
    return filteredAll.slice(0, visibleCount);
  }, [filteredAll, limit, visibleCount]);

  const hasMore = limit
    ? filteredAll.length > limit
    : filteredAll.length > visibleCount;

  const handleViewMore = () => {
    if (limit) {
      navigate("/products");
    } else {
      setVisibleCount((prev) => prev + PAGE_SIZE);
    }
  };

  const isSearching = search.trim().length > 0 || activeCat !== "All";

  if (loading)
    return <div className={styles.loading}>{t("products.loading")}</div>;

  return (
    <section className={styles.section} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <span className={styles.badge}>{t("hero.badge")}</span>
            <h2 className={styles.title}>{t("topbar.products")}</h2>
            <p className={styles.subtitle}>{t("products.subtitle")}</p>
          </div>
        </div>

        <div className={styles.searchBox}>
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder={t("products.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              className={styles.clearBtn}
              onClick={() => setSearch("")}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        <div className={styles.filters}>
          {categories.map((cat) => (
            <button
              key={cat}
              className={`${styles.filterBtn} ${activeCat === cat ? styles.active : ""}`}
              onClick={() => setActiveCat(cat)}
            >
              {cat === "All" ? t("products.all") : cat}
              {cat !== "All" && (
                <span className={styles.filterCount}>
                  {products.filter((p) => p.family?.name === cat).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {filtered.length > 0 ? (
          <div className={styles.grid}>
            {filtered.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => setSelected(product)}
              />
            ))}
          </div>
        ) : (
          <div className={styles.notFoundBox || styles.guestBox}>
            <div className={styles.guestIcon}>🔍</div>
            <h3>{t("products.notFoundTitle")}</h3>
            <p>{t("products.notFoundDesc")}</p>
            {isSearching && (
              <button
                type="button"
                className={styles.loginBtn}
                onClick={() => {
                  setSearch("");
                  setActiveCat("All");
                }}
              >
                {t("products.clearFilters")}
              </button>
            )}
          </div>
        )}

        {hasMore && (
          <div className={styles.viewMoreWrap}>
            <button className={styles.viewMoreBtn} onClick={handleViewMore}>
              {t("products.viewMore")}{" "}
              <ArrowRight
                size={16}
                style={{ transform: isRtl ? "rotate(180deg)" : "none" }}
              />
            </button>
          </div>
        )}
      </div>

      {selected && (
        <ProductDetailModal
          product={selected}
          onClose={() => setSelected(null)}
          isAuthenticated={isAuthenticated}
        />
      )}
    </section>
  );
}